import { fetchQuery } from 'react-relay';
import { Environment, Store, RecordSource, Network, FetchFunction } from 'relay-runtime';

import type { pricingPageRelayQuery$data } from '@/__generated_contentful__/pricingPageRelayQuery.graphql';

import {
	PricingPageRelayQuery,
	getPricingPageRelayQueryVars,
	shapePricingContentfulResponse,
} from '../../contentful/graphql/pricing-page';
import { dynamicConfigManifest } from '../dynamic-configs/dynamic-config-manifest.ts';
import { getDynamicConfig } from '../dynamic-configs/get-dynamic-config/get-dynamic-config.ts';
import { getEnvironment } from '../environment';

import { PricingContent } from './get-pricing-content-gql.ts';

import type { SupportedLocales } from '../localization';

const CONTENTFUL_SPACE_URL = 'https://graphql.contentful.com/content/v1/spaces/xjcz23wx147q';

const CONTENTFUL_MASTER = {
	url: `${CONTENTFUL_SPACE_URL}/environments/master`,
	accessToken: 'fTu2YoVb3vqTOyvfOZveEriN4V4vgNk9aVYcJuLRfc8',
};

const CONTENTFUL_STAGING = {
	url: `${CONTENTFUL_SPACE_URL}/environments/staging`,
	accessToken: 'JjhwVz5Qb05UNV-3sgJ-5h4JsWSLrkkMV5vdEgfy6_U',
};

export interface GetPricingContentOptions {
	locale?: SupportedLocales;
	pricingTitleInternal?: string;
}

export const getPricingContent = async (
	productId: string,
	opts: GetPricingContentOptions = {},
): Promise<PricingContent | undefined> => {
	const { pricingTitleInternal } = opts;
	const { url, accessToken } = getContentfulSource();

	const fetchGraphQL: FetchFunction = async (request, variables) => {
		const resp = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify({ query: request.text, variables }),
		});
		if (!resp.ok) {
			throw new Error('Response failed.');
		}
		return await resp.json();
	};

	const environment = new Environment({
		store: new Store(new RecordSource({})),
		network: Network.create(fetchGraphQL),
	});

	const pricingDataRaw = await fetchQuery(
		environment,
		PricingPageRelayQuery,
		getPricingPageRelayQueryVars(productId, window.location, pricingTitleInternal),
	)
		.toPromise()
		.then((data) => data)
		.catch((error) => {
			console.log(error);
		});

	const pricingDataRelay = shapePricingContentfulResponse(
		pricingDataRaw as pricingPageRelayQuery$data,
	);
	return pricingDataRelay?.pricings?.edges?.[0]?.node;
};

interface ContentfulSource {
	url: string;
	accessToken: string;
}

/**
 * Returns the Contentful staging source when running outside production and the
 * dynamic config opts in; otherwise falls back to master.
 */
const getContentfulSource = (): ContentfulSource => {
	const isStagingContentEnabled = getDynamicConfig({
		config: dynamicConfigManifest.isPricingPageContentFromStagingEnabled,
		defaultValue: false,
	});

	return getEnvironment() !== 'production' && isStagingContentEnabled
		? CONTENTFUL_STAGING
		: CONTENTFUL_MASTER;
};
